/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check
import EventEmitter from 'eventemitter3';

import Queue from '../../queue/queue';
import StateMachine from '../../state-machine/state-machine';

/**
 * @import { ContentController, ContentControllerEventTypes } from '.'
 */

export class BaseContentController {
  constructor() {
    /** @type {EventEmitter<ContentControllerEventTypes>} */
    this.emitter = new EventEmitter();

    this.lifeCycleQueue = new Queue();
    /** @type {StateMachine} */
    this.stateMachine = new StateMachine({
      init: 'created',
      transitions: [
        {
          name: 'preload',
          from: ['created', 'stopped'],
          to: 'preloaded'
        },
        { name: 'start', from: ['preloaded'], to: 'started' },
        { name: 'restart', from: ['started'], to: 'started' },
        {
          name: 'stop',
          from: ['created', 'preloaded', 'started'],
          to: 'stopped'
        }
      ]
    });
  }

  /** @type {ContentController['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {ContentController['on']} */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * @param {() => Promise<void>} callback
   */
  async preloadCallback(callback) {
    await this.lifeCycleQueue.enqueue(async () => {
      await this.stateMachine.transitAsync(
        'preload',
        async () => await callback()
      );
    });
  }

  /**
   * @param {() => Promise<void>} callback
   */
  async restartCallback(callback) {
    await this.lifeCycleQueue.enqueue(async () => {
      await this.stateMachine.transitAsync(
        'restart',
        async () => await callback()
      );
    });
  }

  /**
   * @param {() => Promise<void>} callback
   */
  async startCallback(callback) {
    await this.lifeCycleQueue.enqueue(async () => {
      await this.stateMachine.transitAsync(
        'start',
        async () => await callback()
      );
    });
  }

  /**
   * @param {() => Promise<void>} callback
   */
  async stopCallback(callback) {
    await this.lifeCycleQueue.enqueue(async () => {
      await this.stateMachine.transitAsync(
        'stop',
        // Úmyslené ignorování chyby. Pokud chyba vznikla při stop,
        // tak aby metoda stop byla přerušena. Ten případ by neměl nastávat.
        async () => await callback().catch(() => {})
      );
    });
  }
}
